import React, {useEffect, useState, useCallback} from "react";
import {Helmet} from "react-helmet";
import {Spin, Modal, List, message} from "antd";
import {Alert} from "@mui/material";
import {jwtDecode} from "jwt-decode";
import useProtecaoDeRota from "../hooks/useProtecaoDeRota";
import {useDashboards} from "../hooks/useDashboards";
import Card from "../components/Card";
import DashboardCard from "../components/DashboardCard";
import DashboardAccessModal from "../components/DashboardAccessModal";
import CategorySection from "../components/CategorySection";
import {CATEGORY_ICONS, SUBCATEGORY_ICONS} from "../constants/dashboardIcons";
import useUserType from "../hooks/useUserType";

function normalize(str) {
    if (!str) return "";
    return str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
}

const PaginaInicio = () => {
    useProtecaoDeRota();
    const userType = useUserType();
    const isAdmin = userType === 'administrador';

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalGroupData, setModalGroupData] = useState([]); // [{dashboard_id, nome_dashboard, usuarios: []}]
    const [modalGroupName, setModalGroupName] = useState("");
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [diasTokenExpira, setDiasTokenExpira] = useState(0);

    const token = localStorage.getItem("token");
    const {usuario_id: userId, nome: nomeUsuario = "Usuário"} = jwtDecode(token || "{}");

    const {
        dashboards, categories, loading, error, handleToggleFavorite, fetchDashboards, getFavoriteDashboards
    } = useDashboards(userId);

    const handleViewAccess = useCallback(async (groupId, groupName) => {
        setIsModalVisible(true);
        setIsModalLoading(true);
        setModalGroupName(groupName);
        setModalGroupData([]);

        try {
            const response = await fetch(
                `https://vision.alfama.com/api/view/usuarios/administrador/grupos_permissao/${groupId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Erro ao buscar dados do departamento`);
            }

            const data = await response.json();

            // Data já vem na estrutura correta do backend
            setModalGroupData(data);
        } catch (err) {
            console.error("Erro ao buscar dados:", err);
            message.error("Erro ao carregar informações de acesso.");
            setModalGroupData([]);
        } finally {
            setIsModalLoading(false);
        }
    }, [token]);

    const getDias = async () => {
        try {
            const response = await fetch("https://vision.alfama.com/api/view/usuarios/administrador/dashboard/token", {
                headers: {
                    Authorization: `Bearer ${token}`, "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const txt = await response.text().catch(() => null);
                throw new Error(`HTTP ${response.status} - ${txt || "Erro ao buscar usuários."}`);
            }

            const data = await response.json();
            console.log(data);
            setDiasTokenExpira(data[0].dataToken);
        } catch (e) {
            console.error(e);
        }
    }

    useEffect(() => {
        if (userId) {
            fetchDashboards();
            getDias();
        }
    }, [userId, fetchDashboards]);

    if (loading) {
        return (<div className="flex items-center justify-center h-screen">
            <Spin size="large"/>
        </div>);
    }

    const favoriteDashboards = getFavoriteDashboards();

    //const diasTokenExpira = getDias();

    return (<>
        <Helmet>
            <title>Vision | Página Principal</title>
        </Helmet>

        <main className="flex-1 w-full p-4 sm:p-8 space-y-8 bg-gray-50 dark:bg-gray-900">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100">
                    Vision | Alfama Foods
                </h1>

                {isAdmin && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 w-full sm:w-auto">
                        {diasTokenExpira > 0 ? (
                            <Alert severity="info" className="text-xs sm:text-sm">
                                {diasTokenExpira} dias para a atualização de TOKEN
                            </Alert>
                        ) : (
                            <Alert severity="warning" className="text-xs sm:text-sm">
                                É necessário atualizar o TOKEN de acesso
                            </Alert>
                        )}
                    </div>
                )}
            </div>

            <p className="mt-2 text-gray-600 dark:text-gray-400">
                Bem-vindo de volta, {nomeUsuario}!
            </p>

            {error ? (<Card title="Erro">
                <div className="text-center text-red-500">{error}</div>
            </Card>) : (<>
                {favoriteDashboards.length > 0 && (<Card title="Meus Favoritos"
                                                         description="Acesse rapidamente os dashboards que você mais usa.">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                        {favoriteDashboards.map((dashboard) => (
                            <DashboardCard key={dashboard.id} dashboard={dashboard}
                                           onToggleFavorite={handleToggleFavorite}/>))}
                    </div>
                </Card>)}

                <div className="space-y-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Todos os
                            Dashboards</h2>
                        <p className="mt-1 text-gray-500 dark:text-gray-400">Navegue por workspace e setor.</p>
                    </div>

                    {categories.sort((a, b) => {
                        const nomeA = normalize(a.name);
                        const nomeB = normalize(b.name);

                        if (nomeA === 'gestao') return -1;
                        if (nomeB === 'gestao') return 1;

                        return nomeA.localeCompare(nomeB);
                    }).map((category) =>
                        (<CategorySection
                            key={category.name} category={category}
                            onToggleFavorite={handleToggleFavorite}
                            onViewAccess={handleViewAccess}
                            icons={{
                                category: CATEGORY_ICONS,
                                subcategory: SUBCATEGORY_ICONS
                            }}
                        />))}
                </div>
            </>)}
        </main>

        <DashboardAccessModal
            visible={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            loading={isModalLoading}
            groupedData={modalGroupData}
            groupName={modalGroupName}
        />
    </>);
};

export default PaginaInicio;
